import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { SACard, SeverityDot, timeAgo } from "@/components/superadmin/SAPrimitives";

export default function SuperAdminAlerts() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/superadmin/alerts");
            setAlerts(data.alerts || []);
            setErr("");
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const iv = setInterval(load, 60_000);
        return () => clearInterval(iv);
    }, []);

    const bySeverity = alerts.reduce((acc, a) => {
        acc[a.severity] = (acc[a.severity] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="space-y-5" data-testid="sa-alerts-page">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[24px] font-semibold tracking-tight">Platform Alerts</h1>
                    <div className="mt-1 text-[12px] text-white/55">
                        {alerts.length} active · {bySeverity.critical || 0} critical · {bySeverity.warning || 0} warning · {bySeverity.info || 0} info
                    </div>
                </div>
                <button
                    onClick={load}
                    data-testid="sa-alerts-refresh"
                    className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-[12px] text-white/75 hover:bg-white/5"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {err && <SACard className="text-red-300">{err}</SACard>}

            <div className="grid grid-cols-1 gap-3">
                {alerts.length === 0 && !loading && (
                    <SACard className="py-10 text-center text-white/50">
                        <AlertTriangle size={16} className="mx-auto mb-2 text-emerald-300" />
                        All clear — no platform-wide alerts right now.
                    </SACard>
                )}
                {alerts.map((a, i) => (
                    <SACard
                        key={i}
                        className="border"
                        style={{
                            borderColor:
                                a.severity === "critical"
                                    ? "rgba(248,113,113,0.35)"
                                    : a.severity === "warning"
                                        ? "rgba(251,191,36,0.35)"
                                        : "rgba(56,189,248,0.35)",
                            background:
                                a.severity === "critical"
                                    ? "rgba(248,113,113,0.08)"
                                    : a.severity === "warning"
                                        ? "rgba(251,191,36,0.06)"
                                        : "rgba(56,189,248,0.06)",
                        }}
                        data-testid={`sa-alert-card-${i}`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                                <SeverityDot severity={a.severity} />
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-wider text-white/55">
                                        {a.severity} · {a.kind}
                                    </div>
                                    <div className="mt-1 text-[14px] font-semibold text-white">{a.message}</div>
                                    <div className="mt-1 text-[11px] text-white/55">
                                        {a.tenant_id && (
                                            <Link
                                                to={`/superadmin/tenants/${a.tenant_id}`}
                                                className="text-[#00C2FF] hover:underline"
                                            >
                                                {a.tenant_name || a.tenant_slug || a.tenant_id}
                                            </Link>
                                        )}
                                        <span className="mx-1.5 text-white/30">·</span>
                                        {timeAgo(a.created_at)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SACard>
                ))}
            </div>
        </div>
    );
}
